import React from "react";
import PropTypes from "prop-types";

// HELPERS
import {createClassName, validateNumber} from "@helpers/utils";


export default class TextField extends React.PureComponent {
	static propTypes = {
		style: PropTypes.object,
		id: PropTypes.string,
		name: PropTypes.string,
		label: PropTypes.string,
		type: PropTypes.string.isRequired,
		placeholder: PropTypes.string,
		required: PropTypes.bool,
		disabled: PropTypes.bool.isRequired,
		readOnly: PropTypes.bool.isRequired,
		hideRequiredSymbol: PropTypes.bool,
		value: PropTypes.any,
		valid: PropTypes.bool,
		tooltip: PropTypes.string,
		autoComplete: PropTypes.bool.isRequired,
		decimalSeparator: PropTypes.string.isRequired,
		onChange: PropTypes.func,
		onFocus: PropTypes.func,
		onBlur: PropTypes.func,
	};
	static defaultProps = {
		type: "text",
		disabled: false,
		readOnly: false,
		autoComplete: true,
		decimalSeparator: ".",
	};

	state = {
		filled: false,
		focused: false,
		autofilled: false,
		preventAutoComplete: !this.props.autoComplete,
	};

	render() {
		const {props, state} = this;
		const {style, id, name, label, type, placeholder, disabled, required, readOnly, hideRequiredSymbol, autoComplete, value, valid, tooltip} = props;
		const classes = createClassName(props.className, {
			"TextField": true,
			[type]: true,
			"focus": state.focused,
			"disabled": disabled,
			"placeholder": !!placeholder,
			"filled": state.filled || !!value || state.autofilled,
			"read-only": readOnly,
			"required": required,
			"tooltip": !!tooltip,
			"valid": valid === true,
			"invalid": valid === false,
			"autofilled": state.autofilled,
			"prevent-autocomplete": state.preventAutoComplete,
		});

		return (
			<div className={classes} style={style}>
				<label className="TextField-label">{(valid === false && props.invalidMessage) || label}</label>
				<input
					ref={ref => this.input = ref}
					className="TextField-input"
					id={id}
					name={name}
					type={type || "text"}
					placeholder={placeholder}
					defaultValue={props.defaultValue}
					value={value}
					autoComplete={autoComplete ? "on" : "off"}
					required={required === true}
					disabled={disabled}
					readOnly={!state.preventAutoComplete || !!value ? readOnly : true}
					onFocus={!disabled && !readOnly ? this._onFocus : undefined}
					onBlur={!disabled && !readOnly ? this._onBlur : undefined}
					onChange={!disabled ? this._onChange : undefined}
					onKeyPress={type === "number" ? this._onNumberKeyPress : undefined}
					onPaste={type === "number" ? this._onNumberPaste : undefined}
					onAnimationStart={!disabled && !state.preventAutoComplete ? this._onAnimationStart : undefined}
				/>
				{!!tooltip ?
					<TextFieldTooltip text={tooltip}/>
					: valid === true ?
						<TextFieldStatus icon="check"/>
						: required === true && !hideRequiredSymbol ?
							<TextFieldStatus requiredSymbol/>
							: valid === false ?
								<TextFieldStatus icon="error"/>
								: null}
			</div>
		);
	}

	// Internal methods
	_onFocus = () => {
		const {onFocus} = this.props;
		this.setState({
			focused: true,
			autofilled: false,
			preventAutoComplete: false
		}, onFocus ? () => onFocus() : undefined);
	};

	_onBlur = () => {
		const {onBlur} = this.props;
		this.setState({focused: false}, onBlur ? () => onBlur() : undefined);
	};

	_onChange = ({target}) => {
		const {props} = this;
		const {type, onChange} = props;

		if (onChange) {
			if (type === "number") {
				const {decimalSeparator} = props;
				const parsedValue = target.value !== "" ? parseFloat(decimalSeparator !== "." ? target.value.replace(decimalSeparator, ".") : target.value) : null;

				onChange(target.value, parsedValue);
			} else {
				onChange(target.value, null);
			}
		}
	};

	_onNumberKeyPress = (e) => {
		const isNumeric = e.key >= 0 && e.key <= 9;
		const isDecimalSeparator = e.key === this.props.decimalSeparator || e.key === ".";
		const isMinusSign = e.key === "-";
		if (!isNumeric && !isDecimalSeparator && !isMinusSign)
		{
			e.preventDefault();
		}
	};

	_onNumberPaste = (e) => {
		const {onChange} = this.props;
		const data = e.clipboardData.getData("text/plain");
		const isValid = validateNumber(data) && !(/[eE]/.test(data));
		if (!isValid) {
			e.preventDefault();
			if (onChange) onChange("", null);
		}
	};

	_onAnimationStart = ({target, animationName}) => {
		switch (animationName) {
			case "onTextFieldAutoFillStart":
				return this.setState({autofilled: true});
			case "onTextFieldAutoFillCancel":
				return this.setState({autofilled: false});
			default:
				return;
		}
	}
}


// PRIVATE COMPONENTS
function TextFieldTooltip({text, onClick}) {
	return (
		<div className="TextField-tooltip">
			<div className="TextField-tooltip-icon">?</div>
			<div className="TextField-tooltip-text">{text}</div>
		</div>
	);
}

function TextFieldStatus({icon, requiredSymbol}) {
	return (
		<div className="TextField-status">
			<div className={createClassName("", {
				"icon": !!icon,
				"material-icons": !!icon,
				"required-symbol": requiredSymbol === true,
			})}>{requiredSymbol === true ? "*" : icon}</div>
		</div>
	);
}


// WEBPACK FOOTER //
// ./src/components/TextField/TextField.js